import * as THREE from 'three';

/**
 * Lusion-style liquid hover for project images.
 *
 * One shared WebGL canvas is moved into whichever image frame the cursor is over. It runs a small velocity
 * field (semi-Lagrangian advection + dissipation): the cursor "stirs" it, and the image is drawn displaced
 * by that field, with a touch of colour fringing where it flows fastest. When the cursor stops or leaves,
 * the liquid settles back and the canvas is removed — it renders the exact same crop as the <img> beneath,
 * so handing over is invisible.
 */

/** Sim grid width (height follows the frame's aspect) */
const SIM_WIDTH = 160;
/** How much the cursor's motion pushes the liquid */
const FORCE = 0.22;
/** Max injected velocity (uv/s) so fast flicks never tear the image */
const MAX_FORCE = 1.6;
/** Brush radius (uv², gaussian) — broad and soft, so the flow reads as a gentle swell, not a smear */
const RADIUS = 0.018;
/** Velocity kept per 60fps frame — a long, slow, calm tail */
const DISSIPATION = 0.978;
/** How far the image is pulled along the flow */
const STRENGTH = 0.005;
/** Cursor force smoothing per 60fps frame (lower = silkier, more delayed) */
const FORCE_SMOOTHING = 0.08;
/** How fast the liquid carries itself along (lower = slower, more viscous drift) */
const FLOW_SPEED = 0.5;
/** Keep rendering this long after the last movement before handing back to the <img> */
const SETTLE_MS = 2600;

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const SIM_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D uVel;
  uniform float uDt;
  uniform float uDiss;
  uniform vec2 uMouse;
  uniform vec2 uForce;
  uniform float uRadius;
  uniform float uAspect;
  void main() {
    vec2 v = texture2D(uVel, vUv).xy;
    vec2 vel = texture2D(uVel, vUv - v * uDt).xy * uDiss;
    vec2 d = vUv - uMouse;
    d.x *= uAspect;
    vel += uForce * exp(-dot(d, d) / uRadius);
    gl_FragColor = vec4(vel, 0.0, 1.0);
  }
`;

const DRAW_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D uVel;
  uniform sampler2D uImg;
  uniform vec4 uRect;   // image box inside the frame (x, y, w, h), top-left origin, in frame units
  uniform vec4 uCover;  // visible part of the texture (x, y, w, h) — object-fit: cover
  uniform float uStrength;

  vec3 sampleImg(vec2 frameUv) {
    vec2 p = vec2(frameUv.x, 1.0 - frameUv.y);
    vec2 box = (p - uRect.xy) / uRect.zw;
    vec2 t = uCover.xy + clamp(box, 0.0, 1.0) * uCover.zw;
    return texture2D(uImg, vec2(t.x, 1.0 - t.y)).rgb;
  }

  void main() {
    vec2 v = texture2D(uVel, vUv).xy;
    vec2 shift = v * uStrength;
    float fringe = smoothstep(0.0, 1.5, length(v)) * 0.0012;
    vec2 dir = length(v) > 0.0001 ? normalize(v) : vec2(0.0);
    vec2 uv = vUv - shift;
    float r = sampleImg(uv - dir * fringe).r;
    float g = sampleImg(uv).g;
    float b = sampleImg(uv + dir * fringe).b;
    gl_FragColor = vec4(r, g, b, 1.0);
  }
`;

type Pass = { scene: THREE.Scene; mat: THREE.ShaderMaterial };

interface Session {
  frame: HTMLElement;
  img: HTMLImageElement;
  tex: THREE.Texture;
}

class FluidDistort {
  private renderer: THREE.WebGLRenderer | null = null;
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private sim!: Pass;
  private draw!: Pass;
  private targets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget] | null = null;
  private textures = new Map<string, Promise<THREE.Texture | null>>();
  private session: Session | null = null;
  private raf = 0;
  private last = 0;
  private lastMove = 0;
  private hovering = false;
  private mouse = { x: 0.5, y: 0.5, px: 0.5, py: 0.5, moved: false };
  private force = { x: 0, y: 0 };

  /** Can this browser run it at all? */
  readonly supported =
    typeof window !== 'undefined' && !!document.createElement('canvas').getContext('webgl2');

  /** Load (once) a CORS-safe texture for this image; resolves null if the host doesn't allow it */
  texture(src: string): Promise<THREE.Texture | null> {
    let p = this.textures.get(src);
    if (!p) {
      p = new Promise((resolve) => {
        const loader = new THREE.TextureLoader();
        loader.setCrossOrigin('anonymous');
        // Separate URL so a cached non-CORS response for the <img> can't block this request
        const url = `${src}${src.includes('?') ? '&' : '?'}fx=1`;
        loader.load(
          url,
          (tex) => {
            tex.minFilter = THREE.LinearFilter;
            tex.magFilter = THREE.LinearFilter;
            tex.generateMipmaps = false;
            resolve(tex);
          },
          undefined,
          () => resolve(null),
        );
      });
      this.textures.set(src, p);
    }
    return p;
  }

  private ensure() {
    if (this.renderer) return this.renderer;
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance' });
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // pass colours straight through
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const c = renderer.domElement;
    c.setAttribute('aria-hidden', 'true');
    Object.assign(c.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '1',
    });

    const quad = new THREE.PlaneGeometry(2, 2);
    const make = (frag: string, uniforms: Record<string, THREE.IUniform>): Pass => {
      const mat = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: frag, uniforms, depthTest: false, depthWrite: false });
      const scene = new THREE.Scene();
      scene.add(new THREE.Mesh(quad, mat));
      return { scene, mat };
    };
    this.sim = make(SIM_FRAG, {
      uVel: { value: null },
      uDt: { value: 0.016 },
      uDiss: { value: DISSIPATION },
      uMouse: { value: new THREE.Vector2() },
      uForce: { value: new THREE.Vector2() },
      uRadius: { value: RADIUS },
      uAspect: { value: 1 },
    });
    this.draw = make(DRAW_FRAG, {
      uVel: { value: null },
      uImg: { value: null },
      uRect: { value: new THREE.Vector4(0, 0, 1, 1) },
      uCover: { value: new THREE.Vector4(0, 0, 1, 1) },
      uStrength: { value: STRENGTH },
    });
    this.renderer = renderer;
    return renderer;
  }

  private resizeTo(frame: HTMLElement) {
    const renderer = this.ensure();
    const w = Math.max(frame.clientWidth, 1);
    const h = Math.max(frame.clientHeight, 1);
    renderer.setSize(w, h, false);
    const simH = Math.max(Math.round((SIM_WIDTH * h) / w), 16);
    const opts = { type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false };
    if (!this.targets || this.targets[0].height !== simH) {
      this.targets?.forEach((t) => t.dispose());
      this.targets = [new THREE.WebGLRenderTarget(SIM_WIDTH, simH, opts), new THREE.WebGLRenderTarget(SIM_WIDTH, simH, opts)];
    }
    // Fresh, still liquid for every new image
    this.targets.forEach((t) => {
      renderer.setRenderTarget(t);
      renderer.setClearColor(0x000000, 1);
      renderer.clear();
    });
    renderer.setRenderTarget(null);
    this.sim.mat.uniforms.uAspect.value = w / h;
  }

  /** Cursor entered an image frame. Returns false if the effect can't run for this image. */
  async enter(frame: HTMLElement, img: HTMLImageElement, clientX: number, clientY: number): Promise<boolean> {
    if (!this.supported) return false;
    this.hovering = true;
    this.setPointer(frame, clientX, clientY, true);
    const tex = await this.texture(img.currentSrc || img.src);
    if (!tex || !this.hovering) return !!tex;

    if (this.session?.frame !== frame) {
      this.resizeTo(frame);
      this.session = { frame, img, tex };
      this.draw.mat.uniforms.uImg.value = tex;
      frame.appendChild(this.ensure().domElement);
    }
    this.lastMove = performance.now();
    if (!this.raf) {
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }
    return true;
  }

  move(frame: HTMLElement, clientX: number, clientY: number) {
    if (this.session && this.session.frame !== frame) return;
    this.setPointer(frame, clientX, clientY, false);
    this.mouse.moved = true;
    this.lastMove = performance.now();
  }

  leave(frame: HTMLElement) {
    if (this.session && this.session.frame !== frame) return;
    this.hovering = false;
  }

  private setPointer(frame: HTMLElement, clientX: number, clientY: number, jump: boolean) {
    const r = frame.getBoundingClientRect();
    const x = (clientX - r.left) / r.width;
    const y = 1 - (clientY - r.top) / r.height; // GL uv (y up)
    if (jump) {
      this.mouse.px = x;
      this.mouse.py = y;
    }
    this.mouse.x = x;
    this.mouse.y = y;
  }

  private tick = (now: number) => {
    const s = this.session;
    const renderer = this.renderer;
    if (!s || !renderer || !this.targets) {
      this.raf = 0;
      return;
    }
    const dt = Math.min((now - this.last) / 1000, 0.05) || 0.016;
    this.last = now;

    // Cursor force = its velocity in uv/s, clamped
    const m = this.mouse;
    let fx = 0;
    let fy = 0;
    if (m.moved && this.hovering) {
      fx = ((m.x - m.px) / dt) * FORCE;
      fy = ((m.y - m.py) / dt) * FORCE;
      const len = Math.hypot(fx, fy);
      if (len > MAX_FORCE) {
        fx = (fx / len) * MAX_FORCE;
        fy = (fy / len) * MAX_FORCE;
      }
    }
    m.px = m.x;
    m.py = m.y;
    m.moved = false;
    // Ease the force in and out so jerky mouse input becomes a smooth push
    const k = 1 - Math.pow(1 - FORCE_SMOOTHING, dt * 60);
    this.force.x += (fx - this.force.x) * k;
    this.force.y += (fy - this.force.y) * k;
    fx = this.force.x;
    fy = this.force.y;

    // Simulate
    const [read, write] = this.targets;
    const su = this.sim.mat.uniforms;
    su.uVel.value = read.texture;
    su.uDt.value = dt * FLOW_SPEED;
    su.uDiss.value = Math.pow(DISSIPATION, dt * 60);
    su.uMouse.value.set(m.x, m.y);
    su.uForce.value.set(fx, fy);
    renderer.setRenderTarget(write);
    renderer.render(this.sim.scene, this.camera);
    this.targets = [write, read];

    // Draw the image exactly where the <img> sits (follows parallax + reveal scale)
    const fr = s.frame.getBoundingClientRect();
    const ir = s.img.getBoundingClientRect();
    const du = this.draw.mat.uniforms;
    du.uVel.value = write.texture;
    du.uRect.value.set((ir.left - fr.left) / fr.width, (ir.top - fr.top) / fr.height, ir.width / fr.width, ir.height / fr.height);
    const img = s.tex.image as HTMLImageElement;
    const scale = Math.max(ir.width / img.width, ir.height / img.height);
    const cw = ir.width / (img.width * scale);
    const ch = ir.height / (img.height * scale);
    du.uCover.value.set((1 - cw) / 2, (1 - ch) / 2, cw, ch);
    renderer.setRenderTarget(null);
    renderer.render(this.draw.scene, this.camera);

    // Once the liquid has settled and the cursor is gone (or idle), hand back to the <img>
    if (!this.hovering && now - this.lastMove > SETTLE_MS) {
      renderer.domElement.remove();
      this.session = null;
      this.raf = 0;
      return;
    }
    this.raf = requestAnimationFrame(this.tick);
  };
}

let instance: FluidDistort | null = null;
/** Shared instance — only one image is ever being stirred at a time */
export function fluidDistort(): FluidDistort {
  if (!instance) instance = new FluidDistort();
  return instance;
}
